import axios from 'axios';

// Define the base URL for the agent API
const API_URL = process.env.REACT_APP_AGENT_API_URL || 'http://localhost:8008';

// Define types for the API requests and responses
interface AgentRequest {
  query: string;
  conversation_id?: string;
  provider?: string;
  chain_name: string;
  additional_inputs?: Record<string, any>;
  return_intermediate?: boolean;
  attachments?: AttachmentInfo[];
}

interface AttachmentInfo {
  filename: string;
  content_type: string;
  size: number;
  content: string;
}

interface AgentResponse {
  response: string;
  conversation_id: string;
  provider: string;
  chain_name: string;
  intermediate_outputs?: Record<string, any>;
}

interface SupportedFileTypes {
  supported_types: Record<string, string[]>;
  max_file_size: number;
  max_file_size_mb: number;
}



// Add types for background job result
export interface QueryJobResult {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  result?: AgentResponse;
  error?: string;
}

// Helper to poll for job result
export async function pollQueryJobResult(jobId: string, interval = 1500, timeout = 60000): Promise<AgentResponse> {
  const start = Date.now();
  while (true) {
    const res = await axios.get<QueryJobResult>(`${API_URL}/agent/query/${jobId}`);
    if (res.data.status === 'completed' && res.data.result) {
      return res.data.result;
    }
    if (res.data.status === 'failed') {
      throw new Error(res.data.error || 'AI generation failed');
    }
    if (res.data.status === 'cancelled') {
      throw new Error('AI generation was cancelled');
    }
    if (Date.now() - start > timeout) {
      throw new Error('AI generation timed out');
    }
    await new Promise(r => setTimeout(r, interval));
  }
}

/**
 * Cancel a running query job
 * 
 * @param jobId The job ID to cancel
 * @returns Promise with cancellation result
 */
export const cancelQueryJob = async (jobId: string): Promise<{ status: string; message: string }> => {
  try {
    const response = await axios.delete(`${API_URL}/agent/query/${jobId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error cancelling query job:', error);
    throw new Error(error.response?.data?.detail || 'Failed to cancel query job');
  }
};

/**
 * Submit a content generation request to the AI agent (background job).
 * Returns the job_id for polling.
 */
export const generateActivityContent = async (
  prompt: string,
  chainName: string = 'content_analyzer',
  context?: any,
  files?: File[]
): Promise<string> => {
  let jobId: string | undefined;
  if (files && files.length > 0) {
    const formData = new FormData();
    formData.append('query', prompt);
    formData.append('chain_name', chainName);
    formData.append('return_intermediate', 'true');
    formData.append('background', 'true');
    if (context) {
      formData.append('additional_inputs', JSON.stringify({ context }));
    }
    files.forEach((file) => {
      formData.append('files', file);
    });
    const res = await axios.post<{ job_id: string }>(
      `${API_URL}/agent/query`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    jobId = res.data.job_id;
  } else {
    const request: AgentRequest = {
      query: prompt,
      chain_name: chainName,
      additional_inputs: context ? { context } : {},
      return_intermediate: true,
      // @ts-ignore
      background: true
    };
    const res = await axios.post<{ job_id: string }>(
      `${API_URL}/agent/query`,
      request
    );
    jobId = res.data.job_id;
  }
  if (!jobId) throw new Error('No job_id returned from AI agent');
  return jobId;
};

/**
 * Get available AI providers
 * 
 * @returns Promise with list of available providers
 */
export const getAvailableProviders = async (): Promise<string[]> => {
  try {
    const response = await axios.get<string[]>(`${API_URL}/agent/providers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching AI providers:', error);
    return ['openai']; // Default fallback
  }
};

/**
 * Get available chain configurations
 * 
 * @returns Promise with list of available chains
 */
export const getAvailableChains = async (): Promise<any[]> => {
  try {
    const response = await axios.get<any[]>(`${API_URL}/agent/chains`);
    return response.data;
  } catch (error) {
    console.error('Error fetching AI chains:', error);
    return []; // Empty fallback
  }
};

/**
 * Get supported file types for uploads
 * 
 * @returns Promise with supported file types information
 */
export const getSupportedFileTypes = async (): Promise<SupportedFileTypes> => {
  try {
    const response = await axios.get<SupportedFileTypes>(`${API_URL}/agent/supported-file-types`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supported file types:', error);
    // Return default supported types
    return {
      supported_types: {
        'text/plain': ['.txt'],
        'text/markdown': ['.md', '.markdown'],
        'application/pdf': ['.pdf'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/json': ['.json'],
        'text/csv': ['.csv']
      },
      max_file_size: 10 * 1024 * 1024, // 10MB
      max_file_size_mb: 10
    };
  }
};

export default {
  generateActivityContent,
  getAvailableProviders,
  getAvailableChains,
  getSupportedFileTypes
}; 